import { ActionIcon, Group, Stack } from '@mantine/core';
import {
	apiQueryKey,
	GenerateProposalsRequest,
	useApiForceRefreshProposalsGeneration,
	useApiGetProposalsGenerationResult,
} from '@repo/api-codegen';
import {
	AvatarSkeleton,
	Button,
	Icon,
	Text,
	TextSkeleton,
} from '@repo/foundations';
import { queryClient } from '../../../../../../../api';
import { ProposalsListModal } from '../../../../../../../components/AIAssistant/Messages/components/ProposalsListModal';
import { WidgetType } from '../../../../../../../interfaces';
import { WIDGET_HEIGHT } from '../../../constants';
import type { IWidgetItemProps } from '../../../types';

const useRefreshAISuggestions = ({ widget }: IWidgetItemProps) => {
	const suggestionType: GenerateProposalsRequest = {
		gen_type:
			widget.type === WidgetType.AI_GLOSSARY_SUGGESTIONS
				? 'glossaries_for_workspace'
				: widget.type === WidgetType.AI_MONITOR_SUGGESTIONS
					? 'monitors_for_workspace'
					: 'missing_descriptions_for_resources',
	};

	const {
		data: proposalsGenerationResult,
		refetch: refetchProposalsGenerationResult,
	} = useApiGetProposalsGenerationResult(
		{
			queryParams: {
				gen_type: suggestionType.gen_type,
			},
		},
		{
			refetchInterval: (data) =>
				data.state.data?.status === 'running' ? 5000 : false,
		}
	);

	const { mutateAsync: forceRefresh } = useApiForceRefreshProposalsGeneration();

	const handleRefresh = async () => {
		await forceRefresh({
			body: {
				gen_type: suggestionType.gen_type,
			},
		});
		await queryClient.invalidateQueries({
			queryKey: apiQueryKey(
				'ai/proposals/proposals_generation/get_by_identifier'
			),
		});
	};

	return { handleRefresh, proposalsGenerationResult };
};

function AISuggestionsSkeleton() {
	return (
		<Stack spacing="sm" p="sm">
			{Array.from({ length: 5 }).map((_, index) => (
				<Group key={index} spacing="xs" align="center" p={0}>
					<AvatarSkeleton size="sm" />
					<Stack spacing="xs" style={{ flex: 1 }}>
						<TextSkeleton size="sm" width="50%" />
						<TextSkeleton size="xs" width="30%" />
					</Stack>
				</Group>
			))}
		</Stack>
	);
}

export function RefreshAISuggestionsIconButton({ widget }: IWidgetItemProps) {
	const { handleRefresh, proposalsGenerationResult } = useRefreshAISuggestions({
		widget,
	});

	if (!proposalsGenerationResult) {
		return null;
	}

	if (
		proposalsGenerationResult.status === 'running' ||
		proposalsGenerationResult.status === 'pending'
	) {
		return (
			<Group spacing="xs">
				<Text color="text/ai/default" size="sm" weight="semibold">
					Generating
				</Text>
				<Icon name="loader2" color="text/ai/default" animation="spin" />
			</Group>
		);
	}

	return (
		<ActionIcon onClick={handleRefresh}>
			<Icon name="refresh" color="text/ai/default" />
		</ActionIcon>
	);
}

export function AISuggestionsWidget({ widget }: IWidgetItemProps) {
	const { handleRefresh, proposalsGenerationResult } = useRefreshAISuggestions({
		widget,
	});

	if (!proposalsGenerationResult) {
		return null;
	}

	if (
		proposalsGenerationResult.status === 'running' ||
		proposalsGenerationResult.status === 'pending'
	) {
		return <AISuggestionsSkeleton />;
	}

	const proposals = proposalsGenerationResult.proposals.filter(
		(proposal) => proposal.status === 'pending'
	);

	if (proposals.length === 0) {
		return (
			<Stack
				spacing="xs"
				p="md"
				align="center"
				justify="center"
				h={WIDGET_HEIGHT / 2}
			>
				<Text color="text/secondary/default">
					No suggestions{' '}
					{proposalsGenerationResult.proposals.length > 0 ? 'left' : 'found'}
				</Text>
				<Group spacing="xs">
					<Button variant="primary" onClick={handleRefresh}>
						Refresh
					</Button>
				</Group>
			</Stack>
		);
	}

	// Only take first 6 proposals
	const displayedProposals = proposals.slice(0, 6);

	return (
		<ProposalsListModal
			data={displayedProposals || []}
			mode={{
				type: 'inline_list',
				noReviewButton: true,
			}}
		/>
	);
}
